import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../api/apiClient';
import InfoBox from '../InfoBox';
import RiskRegister from '../RiskRegister';

const STATUS_STYLE = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-900',
  yellow: 'bg-accent/10 text-accent-text border-accent/40',
  red: 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800',
  gray: 'bg-surface-raised text-ink-secondary border-border-strong',
};

export default function AIActTab({ onGoToTab }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRisks, setShowRisks] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiService.getAuditAIAct();
      setData(r);
    } catch (e) {
      setError(e?.message || 'Network error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateReport = useCallback(() => {
    if (!data) return;
    const bundle = {
      format: 'neorag-ai-act-snapshot',
      generated_at: new Date().toISOString(),
      ...data,
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neorag-ai-act-snapshot-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const downloadSchema = useCallback(async () => {
    try {
      const r = await apiService.getAuditSchema();
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'neorag-audit-schema.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {}
  }, []);

  return (
    <div>
      <InfoBox tabKey="aiAct" />

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={generateReport}
                className="text-label tracking-widest px-4 py-2 border border-accent/40 bg-accent/10 text-accent-text rounded hover:bg-accent/20">
          {t('traceability:actions.generateReport')}
        </button>
        <button onClick={downloadSchema}
                className="text-label tracking-widest px-4 py-2 border border-border-strong text-ink-secondary rounded hover:text-accent-text">
          {t('traceability:actions.downloadSchema')}
        </button>
      </div>

      {loading && <div className="text-ink-muted">···</div>}
      {!loading && error && (
        <div className="rounded border border-signal-error/40 bg-signal-error/10 text-signal-error px-4 py-3 text-sm">
          {error}
          <button onClick={load} className="ml-3 underline hover:no-underline">
            {t('ui:refresh')}
          </button>
        </div>
      )}
      {!loading && !error && data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data.cards || []).map((card) => (
            <ArticleCard
              key={card.article}
              card={card}
              onGoToTab={onGoToTab}
              onOpenRisks={card.details_kind === 'risks' ? () => setShowRisks(true) : undefined}
            />
          ))}
        </div>
      )}

      {showRisks && <RiskRegister onClose={() => setShowRisks(false)} />}
    </div>
  );
}

function ArticleCard({ card, onGoToTab, onOpenRisks }) {
  const { t } = useTranslation();
  const style = STATUS_STYLE[card.status] || STATUS_STYLE.gray;
  const statusKey =
    card.status === 'green' ? 'statusGreen'
    : card.status === 'yellow' ? 'statusYellow'
    : card.status === 'red' ? 'statusRed'
    : 'statusGray';

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-label tracking-widest text-ink-muted uppercase">{card.article}</div>
          <h3 className="text-base font-semibold text-ink mt-1">{card.title}</h3>
        </div>
        <span className={`text-label tracking-widest px-2 py-1 rounded border ${style}`}>
          {t(`traceability:aiAct.${statusKey}`)}
        </span>
      </header>

      <Section heading={t('traceability:aiAct.requirement')} body={card.requirement} />
      <Section heading={t('traceability:aiAct.implementation')} body={card.implementation} />
      <Section
        heading={t('traceability:aiAct.whereToSee')}
        body={t(`traceability:aiAct.where.${card.where_to_see}`, { defaultValue: card.where_to_see })}
      />

      <div className="mb-3">
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          {t('traceability:aiAct.evidence')}
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          {Object.entries(card.evidence || {}).map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-ink-muted">{k}</dt>
              <dd className="text-ink-secondary font-mono">
                {typeof v === 'object' && v !== null
                  ? <code className="text-[10px]">{JSON.stringify(v).slice(0, 120)}</code>
                  : String(v)}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </div>

      <div className="flex flex-wrap gap-2">
        {onOpenRisks && (
          <button onClick={onOpenRisks}
                  className="text-label tracking-widest px-3 py-1 border border-accent/40 bg-accent/10 text-accent-text rounded hover:bg-accent/20">
            {t('traceability:risks.openButton')}
          </button>
        )}
        {card.where_to_see && card.where_to_see !== 'risks' && (
          <button onClick={() => onGoToTab(card.where_to_see)}
                  className="text-label tracking-widest px-3 py-1 border border-border-strong rounded hover:text-accent-text">
            {t('traceability:actions.seeEvidence')}
          </button>
        )}
      </div>
    </article>
  );
}

function Section({ heading, body }) {
  return (
    <div className="mb-3">
      <div className="text-label tracking-widest text-ink-muted uppercase mb-1">{heading}</div>
      <div className="text-sm text-ink-secondary leading-relaxed">{body}</div>
    </div>
  );
}
